import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import DocumentIcon from '@mui/icons-material/Description';
import ChatIcon from '@mui/icons-material/Chat';

// Icon mapping
const MarkdownIconMap: Record<string, JSX.Element> = {
  paperclip: <AttachFileIcon fontSize="inherit" style={{ verticalAlign: 'middle' }} />,
  document: <DocumentIcon fontSize="inherit" style={{ verticalAlign: 'middle' }} />,
  chat: <ChatIcon fontSize="inherit" style={{ verticalAlign: 'middle' }} />,
};

// Helper function to process text and replace icon placeholders
const processTextWithIcons = (text: string): (string | JSX.Element)[] => {
  const regex = /\[icon:([a-zA-Z0-9_-]+)\]/g;
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const [fullMatch, iconName] = match;
    const start = match.index;

    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    const textBefore = text.slice(0, start).trim();
    const remainingText = text.slice(start + fullMatch.length);
    const hasTextAfter = remainingText.trim().length > 0;
    const isAtBeginning = textBefore.length === 0;
    const needsMargin = isAtBeginning && hasTextAfter;

    if (MarkdownIconMap[iconName]) {
      const iconElement = React.cloneElement(MarkdownIconMap[iconName], {
        key: `icon-${start}`,
        style: {
          ...MarkdownIconMap[iconName].props.style,
          marginRight: needsMargin ? '4px' : undefined,
        },
      });
      parts.push(iconElement);
    } else {
      parts.push(`[icon:${iconName}]`);
    }

    lastIndex = start + fullMatch.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

const COL_WIDE_MARKER = /\s*\{\.col-wide\}\s*$/i;
const ALLOWED_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/** Strip `{.col-wide}` marker appended by backend Vera table post-processing. */
function tableCellContent(children: React.ReactNode): {
  content: React.ReactNode;
  isWide: boolean;
} {
  if (typeof children === 'string') {
    const isWide = COL_WIDE_MARKER.test(children);
    return { content: children.replace(COL_WIDE_MARKER, '').trim(), isWide };
  }
  if (Array.isArray(children) && children.length === 1 && typeof children[0] === 'string') {
    const text = children[0] as string;
    const isWide = COL_WIDE_MARKER.test(text);
    return { content: text.replace(COL_WIDE_MARKER, '').trim(), isWide };
  }
  return { content: children, isWide: false };
}

/** Custom renderers that add table styling matching the native app. */
const markdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  a: ({ href, children }) => {
    const safeHref = sanitizeMarkdownHref(href);
    if (!safeHref) return <>{children}</>;
    return (
      <a
        href={safeHref}
        target={safeHref.startsWith('http') ? '_blank' : undefined}
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  },
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', marginTop: 4, marginBottom: 4 }}>
      <table
        className="vera-markdown-table"
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          fontSize: '0.8rem',
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.15)',
          tableLayout: 'fixed',
        }}
      >
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead style={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>{children}</thead>
  ),
  th: ({ children }) => {
    const { content, isWide } = tableCellContent(children);
    return (
      <th
        className={isWide ? 'col-wide' : undefined}
        style={{
          padding: '6px 8px',
          fontWeight: 700,
          textAlign: 'left',
          borderBottom: '1px solid rgba(0,0,0,0.15)',
          whiteSpace: isWide ? 'normal' : 'nowrap',
          minWidth: isWide ? 140 : undefined,
          verticalAlign: 'top',
        }}
      >
        {content}
      </th>
    );
  },
  td: ({ children }) => {
    const { content, isWide } = tableCellContent(children);
    return (
      <td
        className={isWide ? 'col-wide' : undefined}
        style={{
          padding: '6px 8px',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          whiteSpace: isWide ? 'normal' : undefined,
          verticalAlign: 'top',
          wordBreak: isWide ? 'break-word' : undefined,
        }}
      >
        {content}
      </td>
    );
  },
  tr: ({ children }) => <tr>{children}</tr>,
};

function sanitizeMarkdownHref(href: string | undefined): string | null {
  if (!href) return null;
  if (href.startsWith('/') && !href.startsWith('//')) return href;
  try {
    const parsed = new URL(href);
    return ALLOWED_LINK_PROTOCOLS.has(parsed.protocol) ? href : null;
  } catch {
    return null;
  }
}

/**
 * Renders markdown (including GFM tables) with optional [icon:name] substitution.
 * Uses remark-gfm so tables, strikethrough, and task lists all parse correctly.
 *
 * @param variant `inline` — minimal block spacing for text mixed with inline UI (chat links).
 *                `block` — default paragraph/list spacing (AI chat panels).
 */
export const MarkdownRenderer: React.FC<{
  children: string;
  variant?: 'inline' | 'block';
  /** Merged over default markdown components (e.g. text-node linkification). */
  components?: React.ComponentProps<typeof ReactMarkdown>['components'];
}> = ({ children, variant = 'block', components: extraComponents }) => {
  const baseComponents: Components =
    variant === 'inline'
      ? {
          ...markdownComponents,
          p: ({ children: pChildren }: { children?: React.ReactNode }) => <>{pChildren}</>,
        }
      : markdownComponents;
  const components = extraComponents ? { ...baseComponents, ...extraComponents } : baseComponents;

  // If the content contains icon placeholders, fall back to the line-by-line
  // approach so icons still render. Otherwise render the whole block at once
  // so multi-line constructs like tables aren't broken.
  const hasIcons = /\[icon:[a-zA-Z0-9_-]+\]/.test(children);

  if (!hasIcons) {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
        skipHtml
        urlTransform={(url) => sanitizeMarkdownHref(url) ?? ''}
      >
        {children}
      </ReactMarkdown>
    );
  }

  // Legacy line-by-line path for content with [icon:…] placeholders.
  const lines = children.split('\n');
  return (
    <>
      {lines.map((line, lineIndex) => {
        const processedLine = processTextWithIcons(line);

        if (processedLine.length === 1 && typeof processedLine[0] === 'string') {
          return (
            <ReactMarkdown
              key={lineIndex}
              remarkPlugins={[remarkGfm]}
              components={components}
              skipHtml
              urlTransform={(url) => sanitizeMarkdownHref(url) ?? ''}
            >
              {processedLine[0]}
            </ReactMarkdown>
          );
        }

        return (
          <div key={lineIndex}>
            {processedLine.map((part, partIndex) => {
              if (typeof part === 'string') {
                if (part.trim() === '') return part;
                return (
                  <ReactMarkdown
                    key={partIndex}
                    remarkPlugins={[remarkGfm]}
                    components={{ ...components, p: ({ children: pChildren }) => <>{pChildren}</> }}
                    skipHtml
                    urlTransform={(url) => sanitizeMarkdownHref(url) ?? ''}
                  >
                    {part}
                  </ReactMarkdown>
                );
              }
              return part;
            })}
          </div>
        );
      })}
    </>
  );
};
